import { DocumentRequestPopup } from '@modules/transactions/components/DocumentRequestPopup'
import { useLazyGetActiveDocumentRequestsQuery } from 'mmfintech-backend-api'
import { isValidArray, tr } from 'mmfintech-commons'
import { useEffect, useRef } from 'react'

type TModalShow = (props: { header?: React.ReactNode; content: React.ReactNode; options?: any }) => void

// Fetches the merchant's active document requests once per login and, if any are open, shows
// the popup. `documentRequestPopupShown` guards it to a single fetch per isLoggedIn transition —
// not per render — so it doesn't refire on every modalShow/query identity change.
export const useDocumentRequestPopup = (isLoggedIn: boolean, modalShow: TModalShow) => {
  const [fetchActiveDocumentRequests] = useLazyGetActiveDocumentRequestsQuery()
  const documentRequestPopupShown = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only once per isLoggedIn transition, not on every modalShow/fetchActiveDocumentRequests identity change
  useEffect(() => {
    if (isLoggedIn && !documentRequestPopupShown.current) {
      fetchActiveDocumentRequests()
        .unwrap()
        .then(requests => {
          // only mark this login as "checked" once we actually know the answer — a failed
          // fetch (below) leaves this false so it isn't permanently suppressed on a transient error
          documentRequestPopupShown.current = true
          if (isValidArray(requests)) {
            modalShow({
              header: tr('FRONTEND.TRANSACTIONS.DOCUMENT_REQUEST.POPUP.TITLE', 'Documents requested'),
              content: <DocumentRequestPopup requests={requests} />,
              options: { closeOnClickOutside: false }
            })
          }
        })
        .catch(error => console.error('Failed to load active document requests', error))
    } else if (!isLoggedIn) {
      documentRequestPopupShown.current = false
    }
  }, [isLoggedIn])
}
